import { message } from 'antd'
import { CreatePanelRequest } from 'features/drilldown'
import {
  DRILLDOWN_PANEL_CONFIGS,
  DRILLDOWN_PANEL_INITIAL_DATA,
} from 'features/drilldown/components/panelConfigs/PanelConfigs'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useDrilldownPanelCreation } from 'features/drilldown/data/PanelCreationContext'
import {
  FIELD_TYPE_OPERATORS,
  OPERATOR_NAMES,
} from 'features/drilldown/panels/global/filters/DrilldownFilterRow'
import { OntologyContextType, useOntology } from 'features/dynamicOntology'
import { useGeckoNotifications } from 'features/notifications'
import startCase from 'lodash/startCase'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import { ChatInput, MessagesArea } from './components'
import { ChatMessage, useLlm } from './llmApi'
import { LlmPanelData } from './types'
import { stripMarkdown } from './utils'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`

const LATEST_DATA_SCHEMA_VERSION = 1

export const DEFAULT_LLM_PANEL_DATA: LlmPanelData = {
  title: 'AI Helper',
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  isPinned: true,
  messages: [],
  settings: {
    showTimestamps: false,
  },
}

const PANEL_CREATION_INDICATOR = 'CREATE_PANEL_REQUEST'

const useHandlePanelCreationResponse = () => {
  const { createPanels } = useDrilldownPanelCreation()
  const notificationContext = useGeckoNotifications()

  return useCallback(
    (responseResult: string, addMessage: (content: string) => void) => {
      // TODO does this need sanitization?
      const createPanelsRequest = JSON.parse(
        responseResult.split(PANEL_CREATION_INDICATOR)[1].trim()
      )
      createPanels(createPanelsRequest)
      const formattedResponse =
        'Creating panels: ' +
        createPanelsRequest
          .map(
            (request: CreatePanelRequest) =>
              `\n * ${startCase(request.panelType)}`
          )
          .join('\n')
      addMessage(formattedResponse)
      notificationContext.successNotification(
        createPanelsRequest.length > 1
          ? 'Panels created via AI.'
          : 'Panel created via AI.'
      )
    },
    [createPanels, notificationContext]
  )
}

const buildSystemPrompt = (ontology: OntologyContextType) => `
    -------------
    Context
    -------------
    You are a helpful assistant that can answer questions about the types in the following schema:
    ${JSON.stringify(ontology.entryTypeNames)}

    In addition, you know the following fields on those types:
    ${JSON.stringify(ontology.fields)}

    We have an app that can filter down on data and display data. Each panel applies any filters from previous panels.
    The panels we can make are:
    ${JSON.stringify(DRILLDOWN_PANEL_CONFIGS)}
    
    The panels' data schemas may and often should be edited when building panels for users. These are:
    ${JSON.stringify(DRILLDOWN_PANEL_INITIAL_DATA)}

    Note the filter panel schema info here:
    '''
    export type Filter = {
        id: string
        state: FilterState
    }
    export interface FilterState {
        field: DrilldownField
        operator: RolodexHistogramFilterOp
        value?: FilterValue
    }
    ${JSON.stringify(OPERATOR_NAMES)}
    ${JSON.stringify(FIELD_TYPE_OPERATORS)}
    Note that the 'in' operation requires its value to be an array of strings.
    Note that the date filter uses the format YYYY-MM-DD like 2025-06-19.
    '''    

    -------------
    Panel Creation
    -------------
    If a user asks, you can create panels for them by replying with ${PANEL_CREATION_INDICATOR} and then a JSON object with the panel type and initial data override. 
    Do not include any other text in your response other than ${PANEL_CREATION_INDICATOR} and the JSON object. 
    Any JSON provided after the indicator will be parsed directly and used in the app, so please adhere to the exact schema for panel state.
    Eg:
    ${PANEL_CREATION_INDICATOR}
    [{
      "panelType": "map",
      "initialDataOverride": {
        "title": "Map of all sites"
      }
    },
    {
      "panelType": "filter",
      "initialDataOverride": {
        "filters": [
          {
            id: "123",
            filterState: {
                "field": "siteId",
                "op": "=",
                "value": "123"
            }
          }
        ]
      }
    }
    ]

    Additional Instructions:
    * Follow the schema for panel state closely. If a value is non-nullable and can't be undefined, then don't make it null or undefined.
    * If the user asks for a new column, create a column break panel just before the first panel in the new column.
    * Try to give each panel a title that is a good summary of the data it will display.
    * Use the schemas in the context to help with filling in panel state, especially for filters and histograms.
    * Don't make a column break panel unless the user asked for a new column in the current message.
    * Note that inspection types tend to be lowercase in the data ('raut', 'rug', etc).
    * If you want to apply multiple consecutive filters, prefer putting them all into a single panel rather than multiple panels. But only if there are no other panels between them.
    * If a user wants to have a discussion or ask about the ontology or panel schemas, feel free to chat to them and help them out instead of making panels.
`

const RawLlmPanel: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<LlmPanelData>()
  const ontology = useOntology()
  const [inputValue, setInputValue] = useState('')
  const handlePanelCreationResponse = useHandlePanelCreationResponse()

  const messages = panelData.messages
  const setMessages: Dispatch<SetStateAction<ReadonlyArray<ChatMessage>>> =
    useCallback(
      (val) =>
        setPanelData((prev) => {
          const newVal = typeof val === 'function' ? val(prev.messages) : val
          return {
            ...prev,
            messages: newVal,
          }
        }),
      [setPanelData]
    )
  const systemPrompt = useMemo(() => buildSystemPrompt(ontology), [ontology])
  const handleResponse = useCallback(
    (result: string, addMessage: (msg: string) => void) => {
      if (result.includes(PANEL_CREATION_INDICATOR)) {
        handlePanelCreationResponse(result, addMessage)
        return false
      } else {
        return true
      }
    },
    [handlePanelCreationResponse]
  )

  const { sendMessage, isLoading } = useLlm({
    messages,
    setMessages,
    systemPrompt,
    handleResponse,
  })

  const handleSendMessage = useCallback(() => {
    const msgStr = inputValue.trim()
    setInputValue('')
    sendMessage(msgStr)
  }, [inputValue, sendMessage])

  // TODO (xdowns) migrate to Explorer shared copy function
  const handleCopy = useCallback((content: string) => {
    const plainText = stripMarkdown(content)
    navigator.clipboard.writeText(plainText)
    message.success('Response copied to clipboard!')
  }, [])

  return (
    <PanelContainer>
      <MessagesArea
        messages={messages}
        showTimestamps={panelData.settings.showTimestamps}
        isLoading={isLoading}
        onCopy={handleCopy}
      />

      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSendMessage}
        isLoading={isLoading}
      />
    </PanelContainer>
  )
}

export const LlmPanel = React.memo(RawLlmPanel)
